import { skipToken } from '@reduxjs/toolkit/query';
import { useCreateRoute } from 'components/create-dialog/CreateDialogContext';
import { useCustomFields } from 'components/forms/inputs/custom-fields/useCustomFields';
import { isValidEmail } from 'components/forms/inputs/rhf-validators/validators';
import { useModal } from 'components/modals/useModal';
import cloneDeep from 'lodash/cloneDeep';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useForm, useFormState, useWatch } from 'react-hook-form';
import { useGetJobPositionsListQuery } from 'store/api/jobPositions';
import { useGetJobRolesListQuery } from 'store/api/jobRoles';
import { portalApiAxios } from 'store/api/portalAPI.ts';
import { useGetPracticeAreasWithFiltersNotPaginatedQuery } from 'store/api/practice-areas';
import { CustomField } from 'types/customFields';
import { EmployeeForm } from 'types/employees';
import { EntityType } from 'types/entity';
import { FeatureFlag } from 'types/features';
import { SystemRole } from 'types/systemRoles';
import { getValidOptionsFromClassifier } from 'utils/formUtil';
import { t } from 'utils/getTranslations';
import { useLastNameFirst } from 'utils/nameOrder';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import { useClassifiers } from 'utils/useClassifiers';
import { EmployeeSectionId } from '../employeeFormMapping';
import { useEmployeePermissions } from '../useEmployeePermissions';
import { isTimeIntervalOverlapping, mapJobPositionDtosToNamedEntities, mapJobRolesToNamedEntities } from '../util';

const defaultValues: EmployeeForm = {
    firstName: '',
    lastName: '',
    localFirstName: '',
    localLastName: '',
    personTitle: null,
    isActive: true,
    personalIdCode: '',
    workEmailAddress: '',
    workPhone: '',
    mobilePhone: '',
    notes: '',
    previousNames: '',
    manager: null,
    employmentHistory: [],
    affiliations: [],
    systemRoles: [],
    coreTeam: null,
    supportTeams: [],
    chairmanTeams: [],
    practiceAreas: [],
    languagesSpoken: [],
    taskReceivers: [],
    receivingTasksForEmployees: [],
    canImpersonate: [],
    employmentStartDate: null,
    employmentLeaveDate: null,
    birthdayDate: null,
    profilePictureName: null,
    signaturePictureName: null,
    vatRegNum: '',
    genderType: null,
    partnershipPeerGroup: null,
    overwriteSignedByEmployeeId: null,
    cognitoId: null,
    sensitiveInfo: '',
    isPoliticallyExposedPerson: false,
    personalAddress: null,
};

const EMPTY_CUSTOM_FIELDS: CustomField[] = [];

export type EmployeeFormProps = {
    type: 'add' | 'edit';
    initialData?: EmployeeForm;
    systemRoles?: SystemRole[];
    entityCustomFields?: CustomField[];
    /** Locks the active flag; your own, by default. */
    isEditingCurrentUserAndHasPermissionToUpdateEmployees?: boolean;
    /** Whether the employment history is checked as it changes: the form's, and only its own section's. */
    checksEmploymentHistory?: boolean;
    /** The one card being edited, which loads only the lists it shows; the whole form, without one. */
    section?: EmployeeSectionId;
};

/** The employee form's state, shared by the edit and create pages and the record page's sections. */
export const useEmployeeForm = ({
    type,
    initialData,
    systemRoles,
    entityCustomFields = EMPTY_CUSTOM_FIELDS,
    isEditingCurrentUserAndHasPermissionToUpdateEmployees,
    checksEmploymentHistory = true,
    section,
}: EmployeeFormProps) => {
    const { id: employeeId } = useCreateRoute().params;
    const permissions = useEmployeePermissions(employeeId);
    // Targets are saved by the update only; a new employee gets them once created.
    const showTargets =
        useIsFeatureEnabled(FeatureFlag.DASHBOARD_PROGRESS_TRACKING) &&
        permissions.canReadAllPersonalData &&
        type === 'edit';
    const modalActions = useModal();

    const methods = useForm<EmployeeForm>({
        defaultValues: initialData ? { ...defaultValues, ...initialData } : defaultValues,
        values: initialData,
        mode: 'onBlur',
    });

    const { control, setError, clearErrors } = methods;
    const lastNameFirst = useLastNameFirst();

    const {
        customFields,
        mapCustomFieldsToDto,
        customFieldValuesOf,
        isLoading: isLoadingCustomFieldData,
    } = useCustomFields({
        entityType: EntityType.EMPLOYEE,
        fields: entityCustomFields,
    });

    useFormState({ control });
    const employmentHistory = useWatch({ control, name: `employmentHistory` });
    const isActive = useWatch({ control, name: 'isActive' });

    // Validate if email field matches email pattern
    const validEmail = useCallback((val: unknown) => isValidEmail(val, t('validation.email-must-be-valid')), []);
    const emailIsNotUsed = useCallback(
        async (val: unknown) => {
            if (val && typeof val === 'string') {
                if (val !== initialData?.workEmailAddress) {
                    const response = await portalApiAxios.get(`/v1/employees/email/exists/${encodeURIComponent(val)}`);
                    return !response.data || t('validation.email-is-used-by-someone-else');
                }
            }
            return true;
        },
        [initialData?.workEmailAddress]
    );

    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({
        codes: ['GENDER_TYPE', 'PERSON_TITLE', 'LANGUAGE', 'COUNTRY', 'PARTNERSHIP_PEER_GROUP_TYPE'],
    });

    // A skipped list reports isLoading false, so it holds no card on the loader.
    const skipJobLists = !!section && section !== 'employment-history';
    const { data: jobRoles, isLoading: isLoadingJobRoles } = useGetJobRolesListQuery(undefined, {
        skip: skipJobLists,
    });
    const { data: jobPositions, isLoading: isLoadingJobPositions } = useGetJobPositionsListQuery(undefined, {
        skip: skipJobLists,
    });
    const { data: practiceAreaList, isLoading: isLoadingPracticeAreas } =
        useGetPracticeAreasWithFiltersNotPaginatedQuery(
            !section || section === 'competence'
                ? { filters: 'practiceAreaTypeCode==PRACTICE_AREA;showInEmployees==true' }
                : skipToken
        );

    const jobRoleNamedEntities = useMemo(() => mapJobRolesToNamedEntities(jobRoles), [jobRoles]);
    const jobPositionNamedEntities = useMemo(() => mapJobPositionDtosToNamedEntities(jobPositions), [jobPositions]);

    const genderOptions = useMemo(
        () =>
            (getValidOptionsFromClassifier(classifiers['GENDER_TYPE']) as {
                label: string;
                value: string;
            }[]) ?? [],
        [classifiers]
    );

    const personTitleOptions = useMemo(
        () =>
            (getValidOptionsFromClassifier(classifiers['PERSON_TITLE']) as {
                label: string;
                value: string;
            }[]) ?? [],
        [classifiers]
    );

    const partnershipPeerGroupOptions = useMemo(
        () =>
            (getValidOptionsFromClassifier(classifiers['PARTNERSHIP_PEER_GROUP_TYPE']) as {
                label: string;
                value: string;
            }[]) ?? [],
        [classifiers]
    );

    // employmentHistory form validation after every update. An error on a field no section shows would block
    // every other section's save, so a section checks it only where it edits it.
    useEffect(() => {
        if (!checksEmploymentHistory) {
            return;
        }
        const employmentHistoryState = cloneDeep(employmentHistory ?? []);
        if (!employmentHistoryState.length) {
            setError('employmentHistory', { message: t('validation.required-field') });
        } else if (isTimeIntervalOverlapping(employmentHistoryState)) {
            setError('employmentHistory', { message: t('employees.employment.errors.time-interval-overlapping') });
        } else {
            clearErrors('employmentHistory');
        }
    }, [checksEmploymentHistory, employmentHistory, clearErrors, setError]);

    const profilePictureUploadRef = useRef<(() => Promise<string | null>) | null>(null);

    return {
        type,
        employeeId,
        initialData,
        methods,
        systemRoles,
        isEditingCurrentUserAndHasPermissionToUpdateEmployees:
            isEditingCurrentUserAndHasPermissionToUpdateEmployees ??
            (permissions.isSelf && permissions.hasUpdateEmployees),
        ...permissions,
        showTargets,
        modalActions,
        lastNameFirst,
        customFields,
        mapCustomFieldsToDto,
        customFieldValuesOf,
        isActive,
        validEmail,
        emailIsNotUsed,
        classifiers,
        practiceAreaList,
        jobRoleNamedEntities,
        jobPositionNamedEntities,
        genderOptions,
        personTitleOptions,
        partnershipPeerGroupOptions,
        profilePictureUploadRef,
        isLoading:
            isLoadingPracticeAreas ||
            isLoadingJobRoles ||
            isLoadingJobPositions ||
            isLoadingCustomFieldData ||
            isLoadingClassifiers,
    };
};

export type EmployeeFormState = ReturnType<typeof useEmployeeForm>;
